import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { api } from "../lib/api";
import { DashboardSyncProvider } from "../lib/dashboardSync";
import { CardExpandProvider } from "../lib/cardExpand";
import { useIsMobile } from "../lib/useIsMobile";
import { TopHeader } from "../components/dashboard/TopHeader";
import { LeftNav, useNavCollapsed } from "../components/dashboard/LeftNav";
import { InsightCard } from "../components/dashboard/InsightCard";
import { WellnessLabsCard } from "../components/dashboard/WellnessLabsCard";
import { BodyModelCard } from "../components/dashboard/BodyModelCard";
import { BodyIndexCard } from "../components/dashboard/BodyIndexCard";
import { ScheduleCard } from "../components/dashboard/ScheduleCard";
import { AICoachCard } from "../components/dashboard/AICoachCard";
import { TrendingCard } from "../components/dashboard/TrendingCard";
import { HistoryScreen } from "../components/dashboard/HistoryScreen";
import { MobileCarousel } from "../components/dashboard/MobileCarousel";
import { WearableConnectionsPanel } from "../components/dashboard/WearableConnectionsPanel";
import { VersionBadge } from "../components/dashboard/VersionBadge";
import { OpsPortal } from "../components/dashboard/OpsPortal";
import { PractitionerPortal } from "../components/dashboard/PractitionerPortal";

type Props = { session: Session };
type View = "dashboard" | "history" | "ops" | "practitioner";

/* ──────────────────────────────────────────────────────────────────────────
   DESKTOP LAYOUT TUNING — the only knobs you should need.
   Each row is a FIXED pixel height. Cards fill their row exactly, so there is
   never a stretch-gap between rows or dead space inside cards. To make a row
   taller or shorter, change ONE number here. ROW_GAP is the space between rows.
   ────────────────────────────────────────────────────────────────────────── */
const ROW_TRENDING = 150; // top row (sparklines)
const ROW_MIDDLE   = 420; // LifeScribe Body · Daily Insight · Wellness Labs
const ROW_BOTTOM   = 300; // Body Index · Schedule · AI Coach
const ROW_GAP      = 6;   // gap between rows (px)

export default function DashboardPage({ session }: Props) {
  const [state, setState] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeNav, setActiveNav] = useState("home");
  const [view, setView] = useState<View>("dashboard");
  const [showDevicesPanel, setShowDevicesPanel] = useState(false);
  const [toast, setToast] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isPractitioner, setIsPractitioner] = useState(false);

  const { collapsed, toggle: toggleNav } = useNavCollapsed();
  const isMobile = useIsMobile(820);

  useEffect(() => {
    document.body.classList.remove("light");
    return () => { document.body.classList.add("light"); };
  }, []);

  // Detect OAuth callback return params and show toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok  = params.get("wearable_connected");
    const err = params.get("wearable_error");
    if (ok) {
      setToast({ kind: "success", text: `${ok.charAt(0).toUpperCase() + ok.slice(1)} connected.` });
      setShowDevicesPanel(true);
    } else if (err) {
      setToast({ kind: "error", text: `Connection failed: ${err.replace(/_/g, " ")}` });
    }
    if (ok || err) {
      const url = new URL(window.location.href);
      url.searchParams.delete("wearable_connected");
      url.searchParams.delete("wearable_error");
      window.history.replaceState({}, "", url.toString());
      setTimeout(() => setToast(null), 5000);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.dashboard.state();
      setState(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [session.user.id]);

  // Determine whether to surface the Ops portal (admin/ops roles only).
  useEffect(() => {
    api.ops.me().then((r) => setIsStaff(r.isStaff)).catch(() => setIsStaff(false));
    api.practitioner.me().then((r) => setIsPractitioner(r.isPractitioner)).catch(() => setIsPractitioner(false));
  }, [session.user.id]);

  const handleNavSelect = (key: string) => {
    setActiveNav(key);

    if (key === "devices") { setShowDevicesPanel(true); return; }
    if (key === "history") { setView("history"); return; }
    if (key === "ops") { setView("ops"); return; }
    if (key === "practitioner") { setView("practitioner"); return; }

    setView("dashboard");
    if (isMobile) return;

    const targetMap: Record<string, string> = {
      body:  "card-body",
      cal:   "card-schedule",
      coach: "card-coach",
      labs:  "card-labs",
      home:  "card-trending",
    };
    const target = targetMap[key];
    if (!target) return;
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-nav-target="${target}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("nav-highlight");
      void el.offsetWidth;
      el.classList.add("nav-highlight");
      setTimeout(() => el.classList.remove("nav-highlight"), 1500);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-d_text3 text-xs tracking-wider uppercase">Loading LifeScribe…</div>
      </div>
    );
  }

  if (error || !state) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-d_card rounded-2xl border border-d_border/20 p-8 text-center">
          <div className="text-d_coral tracking-wider uppercase text-xs mb-3">Something went wrong</div>
          <p className="text-d_text2 text-sm leading-relaxed mb-6">{error}</p>
          <button onClick={() => load()} className="text-xs text-d_accent hover:text-d_accent/80 underline">Try again</button>
        </div>
      </div>
    );
  }

  const { member, ywi, vitals } = state;
  const trend = state.trend ?? [];
  const plan = state.plan ?? null;
  const visits = (state.visits ?? []).map((v: any) => ({ ...v, date: new Date(v.date) }));
  const hasLiveData = !!vitals && !!vitals.source;

  const navWidth = collapsed ? 48 : 180;

  // ─── Card nodes (shared between desktop grid and mobile carousel) ───
  const cardTrending = <TrendingCard trend={trend} />;
  const cardBody     = <BodyModelCard ywi={ywi} />;
  const cardInsight  = <InsightCard ywi={ywi} vitals={vitals} plan={plan} />;
  const cardLabs     = <WellnessLabsCard ywi={ywi} />;
  const cardVitals   = <BodyIndexCard vitals={vitals} hasLiveData={hasLiveData} onConnectClick={() => setShowDevicesPanel(true)} />;
  const cardSchedule = <ScheduleCard visits={visits} />;
  const cardCoach    = <AICoachCard firstName={member.first_name} />;

  return (
    <DashboardSyncProvider>
      <CardExpandProvider>
        <div
          className="h-screen overflow-hidden grid"
          style={{
            gridTemplateColumns: isMobile ? "1fr" : `${navWidth}px 1fr`,
            gridTemplateRows: "68px minmax(0, 1fr)",
          }}
        >
          {/* Header spans both columns */}
          <div className={isMobile ? "" : "col-span-2"}>
            <TopHeader firstName={member.first_name} lastName={member.last_name} tier={member.tier} />
          </div>

          {!isMobile && (
            <LeftNav active={activeNav} onSelect={handleNavSelect} collapsed={collapsed} onToggleCollapse={toggleNav} isStaff={isStaff} isPractitioner={isPractitioner} />
          )}

          {/* MAIN */}
          {view === "practitioner" ? (
            <main className="overflow-hidden min-h-0">
              <PractitionerPortal />
            </main>
          ) : view === "ops" ? (
            <main className="overflow-hidden min-h-0">
              <OpsPortal />
            </main>
          ) : view === "history" ? (
            <main className="overflow-hidden min-h-0 bg-d_card/30">
              <HistoryScreen />
            </main>
          ) : isMobile ? (
            <main className="overflow-hidden min-h-0">
              <MobileCarousel
                cards={[
                  { key: "trending", label: "Trending",       node: cardTrending },
                  { key: "insight",  label: "Daily Insight",  node: cardInsight  },
                  { key: "body",     label: "LifeScribe Body",node: cardBody     },
                  { key: "labs",     label: "Wellness Labs",  node: cardLabs     },
                  { key: "vitals",   label: "Body Index",     node: cardVitals   },
                  { key: "schedule", label: "Schedule",       node: cardSchedule },
                  { key: "coach",    label: "AI Coach",       node: cardCoach    },
                ]}
              />
            </main>
          ) : (
            /* ─── DESKTOP: fixed-height rows, scroll if they exceed the screen ───
               Every row is an exact pixel height (the constants up top). Each
               card fills its row via h-full (cards already carry it). There is
               no flex-grow, no fr, no min-h-full, no centering — nothing that
               can stretch a row beyond its content or pool leftover space into
               a gap. The page simply lays out three fixed bands and scrolls if
               the viewport is shorter than their sum. */
            <main className="overflow-y-auto overflow-x-hidden p-2">
              <div className="flex flex-col h-full" style={{ gap: ROW_GAP, minHeight: ROW_TRENDING + ROW_MIDDLE + ROW_BOTTOM + ROW_GAP * 2 }}>
                {/* Row 1 — Trending. flex `grow shrink basis` with the fixed
                    height as the basis: rows grow PROPORTIONALLY to fill the
                    main area (no leftover space at the bottom on tall screens)
                    and shrink toward their basis, then the page scrolls, on
                    short screens. The constants up top are the proportions. */}
                <div data-nav-target="card-trending" style={{ flex: `${ROW_TRENDING} 1 0`, minHeight: ROW_TRENDING }}>
                  {cardTrending}
                </div>

                {/* Row 2 — LifeScribe Body · Daily Insight · Wellness Labs */}
                <div
                  className="grid"
                  style={{ gridTemplateColumns: "1fr 1.25fr 1fr", gap: ROW_GAP, flex: `${ROW_MIDDLE} 1 0`, minHeight: ROW_MIDDLE }}
                >
                  <div data-nav-target="card-body" className="min-w-0">{cardBody}</div>
                  <div data-nav-target="card-insight" className="min-w-0">{cardInsight}</div>
                  <div data-nav-target="card-labs" className="min-w-0">{cardLabs}</div>
                </div>

                {/* Row 3 — Body Index · Schedule · AI Coach */}
                <div
                  className="grid"
                  style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: ROW_GAP, flex: `${ROW_BOTTOM} 1 0`, minHeight: ROW_BOTTOM }}
                >
                  <div data-nav-target="card-vitals" className="min-w-0">{cardVitals}</div>
                  <div data-nav-target="card-schedule" className="min-w-0">{cardSchedule}</div>
                  <div data-nav-target="card-coach" className="min-w-0">{cardCoach}</div>
                </div>
              </div>
            </main>
          )}

          {/* MOBILE bottom nav bar */}
          {isMobile && (
            <div className="fixed bottom-0 left-0 right-0 z-[90] bg-d_sf border-t border-d_border/20 flex items-center justify-around px-1 py-1.5">
              {[
                { key: "home",    icon: "⌂",  label: "Home" },
                { key: "history", icon: "📊", label: "History" },
                { key: "coach",   icon: "◎",  label: "Coach" },
                { key: "devices", icon: "⌚",  label: "Devices" },
              ].map((n) => {
                const isActive = (n.key === "history" && view === "history") || (n.key !== "history" && view === "dashboard" && activeNav === n.key);
                return (
                  <button
                    key={n.key}
                    onClick={() => handleNavSelect(n.key)}
                    className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
                      isActive ? "text-d_accent" : "text-d_text3"
                    }`}
                  >
                    <span className="text-base leading-none">{n.icon}</span>
                    <span className="text-[9px] font-semibold tracking-[0.04em] uppercase">{n.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <WearableConnectionsPanel
            open={showDevicesPanel}
            onClose={() => { setShowDevicesPanel(false); setActiveNav("home"); }}
            onChange={() => load()}
          />

          {/* Toast */}
          {toast && (
            <div
              className={`fixed bottom-6 right-6 z-[100] px-4 py-3 rounded-lg border shadow-2xl text-sm font-medium animate-[fadeUp_0.3s_ease] max-w-sm ${
                toast.kind === "success"
                  ? "bg-d_card border-d_accent/40 text-d_accent"
                  : "bg-d_card border-d_coral/40 text-d_coral"
              }`}
            >
              {toast.text}
            </div>
          )}

          {/* Version badge — bottom-right, for visual deploy verification */}
          <VersionBadge />
        </div>
      </CardExpandProvider>
    </DashboardSyncProvider>
  );
}
